import { useState } from 'react';
import { ReactComponent as PlusIcon } from '@elementary/common-ui/assets/20x20/plus-black.svg';
import { Button, FlexColumn } from '@elementary/common-ui/components';
import useAlertDestinations from 'shared/hooks/useAlertDestinations';
import {
  AlertDestinationFormData,
  AlertRuleFormDestination,
} from 'shared/types';
import AlertDestinationForm from './AlertDestinationForm';
import AlertDestinationItem from './AlertDestinationItem';

type Props = {
  setDestinationsValue: (destinations: AlertRuleFormDestination[]) => void;
  destinations: AlertRuleFormDestination[];
};

const DestinationSection = ({ setDestinationsValue, destinations }: Props) => {
  const [editIndex, setEditIndex] = useState<number>();
  const [showAddForm, setShowAddForm] = useState(false);

  const {
    alertDestinationOptions,
    alertProviders,
    getDestinationItem,
    alertDestinationsError,
    alertDestinationsLoading,
  } = useAlertDestinations();

  const handleSubmit = (data: AlertDestinationFormData) => {
    if (data.alertProvider === 'email') {
      const emailDestinations = Array.isArray(data.destination)
        ? data.destination
        : [];

      const newItem = {
        integrationId: alertDestinationOptions['email']?.id || '',
        channelName: JSON.stringify({
          to: emailDestinations.map((dest) => dest.value || ''),
        }),
        type: 'email' as const,
      };

      if (editIndex != null) {
        const newDestinations = [...destinations];
        newDestinations[editIndex] = newItem;
        return setDestinationsValue(newDestinations);
      }

      return setDestinationsValue([...destinations, newItem]);
    }

    const destination = !Array.isArray(data.destination)
      ? data.destination
      : data.destination[0];

    const newItem = {
      integrationId: alertDestinationOptions[data.alertProvider]?.id || '',
      channelName: destination?.value || '',
      type: data.alertProvider,
    };

    if (editIndex != null) {
      const newDestinations = [...destinations];
      newDestinations[editIndex] = newItem;
      return setDestinationsValue(newDestinations);
    }

    return setDestinationsValue([...destinations, newItem]);
  };

  const handleDelete = (index: number) =>
    setDestinationsValue(destinations.filter((_, i) => i !== index));

  const isAddingNewDestination = destinations.length === 0 || showAddForm;
  const showAddDestinationButton = editIndex == null && !isAddingNewDestination;
  const showDestinationForm = editIndex != null || isAddingNewDestination;

  return (
    <FlexColumn sx={{ gap: 1.5 }}>
      {destinations?.map((destination, i) => {
        const destinationItem = getDestinationItem(
          destination.type,
          destination.channelName,
        );

        const channel = Array.isArray(destinationItem)
          ? destinationItem.map((item) => item.label).join(', ')
          : destinationItem?.label || destination.channelName;

        return (
          <AlertDestinationItem
            key={i}
            type={destination.type}
            channel={channel}
            onEdit={() => setEditIndex(i)}
            onDelete={() => handleDelete(i)}
            configurationType={destination?.configurationType}
          />
        );
      })}

      {showDestinationForm && (
        <FlexColumn sx={{ gap: 1.5 }}>
          <AlertDestinationForm
            alertDestination={
              editIndex != null
                ? {
                    type: destinations[editIndex]?.type,
                    channel: getDestinationItem(
                      destinations[editIndex]?.type,
                      destinations[editIndex]?.channelName,
                    ),
                  }
                : undefined
            }
            destinationTypes={alertProviders}
            destinationOptions={alertDestinationOptions}
            autoSubmit={!destinations?.length}
            onSubmit={(data) => {
              handleSubmit(data);
              setEditIndex(undefined);
              setShowAddForm(false);
            }}
            onClose={() => {
              setEditIndex(undefined);
              setShowAddForm(false);
            }}
            providersError={alertDestinationsError}
            isLoadingDestinations={alertDestinationsLoading}
          />
        </FlexColumn>
      )}

      {showAddDestinationButton && (
        <Button
          variant="outlined"
          sx={{ width: 'fit-content' }}
          startIcon={<PlusIcon />}
          onClick={() => setShowAddForm(true)}
        >
          Add alert destination
        </Button>
      )}
    </FlexColumn>
  );
};

export default DestinationSection;
